import React, { useState, useEffect } from 'react';
import { useSeason } from '../context/SeasonContext';
import api from '../api';
import PlayoffPredictionForm from '../components/PlayoffPredictionForm';
import PlayoffScoreGrid from '../components/PlayoffScoreGrid';
import './PlayoffStatusPage.css';

// User colors for score grid visualization
const USER_COLORS = [
  '#00ff88', // Green
  '#ff6b6b', // Red
  '#4dabf7', // Blue
  '#ffd43b', // Yellow
  '#ff6b9d', // Pink
  '#51cf66', // Light Green
  '#ff922b', // Orange
  '#845ef7', // Purple
];

// Score Grid with Real Data Component
const PlayoffScoreGridWithData = ({ playoffId, season }) => {
  const [scoreGridData, setScoreGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadScoreGridData();
    
    // Refresh score data every 30 seconds (matching live scoreboard updates)
    const interval = setInterval(() => {
      loadScoreGridData();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [playoffId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScoreGridData = async () => {
    try {
      setLoading(false); // Only show loading on initial load
      setError(null);
      const response = await api.getPlayoffScoreGrid(playoffId);
      setScoreGridData(response.data);
    } catch (err) {
      console.error('Error loading score grid data:', err);
      setError('Failed to load score grid data');
    }
  };

  if (loading) {
    return (
      <div className="score-grid-section">
        <h3>Score Grid Visualization</h3>
        <div style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>
          Loading score grid data...
        </div>
      </div>
    );
  }

  if (error || !scoreGridData) {
    return (
      <div className="score-grid-section">
        <h3>Score Grid Visualization</h3>
        <div style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>
          {error || 'Score grid data not available'}
        </div>
      </div>
    );
  }

  const { predictions, spread, over_under, homeTeamName, awayTeamName, scoreHistory, currentScore } = scoreGridData;

  if (!predictions || predictions.length === 0) {
    return null; // Don't show grid if no predictions
  }

  // Format predictions for the grid component
  const formattedPredictions = predictions.map((pred, index) => ({
    username: pred.username,
    home: pred.home_team_score || 0,
    away: pred.away_team_score || 0,
    color: USER_COLORS[index % USER_COLORS.length],
  }));

  // Format score history for the grid component
  // Always include initial 0-0, then add actual history
  let formattedScoreHistory = [{ home: 0, away: 0 }];
  if (scoreHistory && scoreHistory.length > 0) {
    // Filter out initial 0-0 if it exists in history, then add the rest
    const actualHistory = scoreHistory.filter(entry => entry.home > 0 || entry.away > 0);
    formattedScoreHistory = [{ home: 0, away: 0 }, ...actualHistory];
  } else if (currentScore && (currentScore.home > 0 || currentScore.away > 0)) {
    // If no history but we have current score, show 0-0 -> current
    formattedScoreHistory = [{ home: 0, away: 0 }, currentScore];
  }

  return (
    <div className="score-grid-section">
      <h3>Score Grid Visualization</h3>
      <PlayoffScoreGrid
        predictions={formattedPredictions}
        gameData={{
          spread: spread || null,
          overUnder: over_under || null,
          homeTeamName: homeTeamName || 'Home Team',
          awayTeamName: awayTeamName || 'Away Team',
          currentScore: currentScore || null,
          scoreHistory: formattedScoreHistory.length > 1 ? formattedScoreHistory : null,
        }}
      />
    </div>
  );
};

const PlayoffStatusPage = () => {
  const { effectiveSeason } = useSeason();
  const [playoff, setPlayoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadPlayoff();
  }, [effectiveSeason]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlayoff = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPlayoffStatus(effectiveSeason);
      if (response.data.exists) {
        setPlayoff(response.data.playoff);
        setShowForm(response.data.playoff.needs_prediction);
      } else {
        setPlayoff(null);
      }
    } catch (err) {
      console.error('Error loading playoff:', err);
      setError('Failed to load playoff information');
    } finally {
      setLoading(false);
    }
  };

  const handlePredictionSuccess = () => {
    setShowForm(false);
    loadPlayoff();
  };

  if (loading) {
    return (
      <div className="playoff-status-page">
        <div className="loading">Loading playoff information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="playoff-status-page">
        <div className="error-message">❌ {error}</div>
      </div>
    );
  }

  if (!playoff) {
    return (
      <div className="playoff-status-page">
        <h2>🏆 League Playoff</h2>
        <p>No active playoff for the current season.</p>
      </div>
    );
  }

  const allSubmitted = playoff.all_submitted;
  const isParticipant = playoff.is_participant;
  const needsPrediction = playoff.needs_prediction;

  return (
    <div className="playoff-status-page">
      <h2>🏆 League Playoff - {playoff.season}</h2>

      {/* Game Info */}
      {playoff.game_id && (
        <div className="game-info-section">
          <h3>Championship Game</h3>
          <p className="game-matchup">
            <strong>{playoff.home_team_name}</strong> vs <strong>{playoff.away_team_name}</strong>
          </p>
        </div>
      )}

      {/* Status */}
      <div className="status-section">
        <h3>Status</h3>
        <p>
          <strong>Status:</strong> {playoff.status === 'active' ? '🟢 Active' : playoff.status === 'completed' ? '✅ Completed' : '⏳ Draft'}
        </p>
        {playoff.prediction_deadline && (
          <p>
            <strong>Deadline:</strong> {new Date(playoff.prediction_deadline).toLocaleString()}
          </p>
        )}
      </div>

      {/* Participants */}
      <div className="participants-section">
        <h3>Participants ({playoff.participants?.length || 0})</h3>
        {playoff.participants && playoff.participants.length > 0 ? (
          <div className="participants-list">
            {playoff.participants.map(participant => (
              <div key={participant.id} className="participant-item">
                <span className="participant-name">{participant.username}</span>
                <span className={`prediction-status ${participant.has_prediction ? 'submitted' : 'pending'}`}>
                  {participant.has_prediction ? '✅ Submitted' : '⏳ Pending'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p>No participants yet.</p>
        )}
      </div>

      {/* Prediction Form or Status */}
      {isParticipant && (
        <div className="prediction-section">
          {needsPrediction && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="make-prediction-button"
            >
              Make Your Prediction
            </button>
          )}
          {showForm && (
            <PlayoffPredictionForm
              playoff={{
                ...playoff,
                current_user_id: parseInt(localStorage.getItem('userId') || '0')
              }}
              onSuccess={handlePredictionSuccess}
              onCancel={() => setShowForm(false)}
            />
          )}
        </div>
      )}

      {/* Predictions Display */}
      {allSubmitted && playoff.predictions && playoff.predictions.length > 0 && (
        <div className="predictions-section">
          <h3>All Predictions</h3>
          <div className="predictions-table">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>{playoff.home_team_name || 'Home'}</th>
                  <th>{playoff.away_team_name || 'Away'}</th>
                  {playoff.status === 'completed' && (
                    <>
                      <th>Score Diff</th>
                      <th>Regular Season Margin</th>
                      <th>Rank</th>
                      {playoff.tiebreaker_method && (
                        <th>Tiebreaker</th>
                      )}
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {playoff.predictions.map(pred => (
                  <tr key={pred.id}>
                    <td>{pred.username}</td>
                    <td>{pred.home_team_score}</td>
                    <td>{pred.away_team_score}</td>
                    {playoff.status === 'completed' && (
                      <>
                        <td>{pred.score_difference?.toFixed(1)}</td>
                        <td>{(pred.accumulated_margin || 0).toFixed(2)}</td>
                        <td>{pred.final_rank}</td>
                        {playoff.tiebreaker_method && (
                          <td style={{ fontSize: '12px' }}>
                            {pred.tiebreaker_display || (
                              <>
                                {playoff.tiebreaker_method === 'margin_then_timing' && (
                                  <>
                                    Margin: {(pred.tiebreaker_margin || pred.accumulated_margin || 0).toFixed(2)}
                                    <br />
                                    Submitted: {new Date(pred.tiebreaker_timing || pred.submitted_at).toLocaleString()}
                                  </>
                                )}
                                {playoff.tiebreaker_method === 'regular_season_margin' && (
                                  <>Margin: {(pred.tiebreaker_margin || pred.accumulated_margin || 0).toFixed(2)}</>
                                )}
                                {playoff.tiebreaker_method === 'prediction_timing' && (
                                  <>Submitted: {new Date(pred.tiebreaker_timing || pred.submitted_at).toLocaleString()}</>
                                )}
                              </>
                            )}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Score Grid Visualization */}
      {allSubmitted && playoff.predictions && playoff.predictions.length > 0 && playoff.game_id && (
        <PlayoffScoreGridWithData playoffId={playoff.id} season={playoff.season} />
      )}

      {/* Results */}
      {playoff.status === 'completed' && (
        <div className="results-section">
          <h3>🏆 Final Results</h3>
          {(() => {
            // Detect if there were any ties (same score_difference) that required tiebreaker
            const sortedPredictions = [...(playoff.predictions || [])].sort((a, b) => 
              (a.final_rank || 999) - (b.final_rank || 999)
            );
            
            // Group by score_difference to find ties
            // Convert to number and round to 1 decimal for grouping (handles floating point precision)
            const scoreGroups = {};
            sortedPredictions.forEach(pred => {
              if (pred.score_difference != null && pred.score_difference !== undefined) {
                const scoreDiff = parseFloat(pred.score_difference);
                if (!isNaN(scoreDiff)) {
                  // Use toFixed(1) as key to handle floating point precision issues
                  const key = scoreDiff.toFixed(1);
                  if (!scoreGroups[key]) {
                    scoreGroups[key] = [];
                  }
                  scoreGroups[key].push(pred);
                }
              }
            });
            
            // Find ties (groups with more than one user with same score_difference)
            const ties = Object.values(scoreGroups).filter(group => group.length > 1);
            const hadTies = ties.length > 0;
            
            // Debug logging - check browser console for this output
            if (playoff.status === 'completed') {
              console.log('Playoff tie detection:', {
                hadTies,
                tiesCount: ties.length,
                tiebreakerMethod: playoff.tiebreaker_method,
                scoreGroups: Object.keys(scoreGroups).map(k => ({ score: k, count: scoreGroups[k].length, users: scoreGroups[k].map(u => u.username) })),
                predictionsCount: sortedPredictions.length
              });
            }
            
            return (
              <>
                {hadTies && playoff.tiebreaker_method && (
                  <div style={{ 
                    marginBottom: '15px', 
                    padding: '12px', 
                    backgroundColor: '#2a2a2a', 
                    borderRadius: '5px',
                    border: '2px solid #ffd700'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#ffd700', fontWeight: 'bold' }}>
                      ℹ️ Tie-Break-Tie-Breaker Applied
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#ccc', lineHeight: '1.5' }}>
                      Multiple users tied with the same prediction score difference. Rankings were determined using the tiebreaker method:
                      {playoff.tiebreaker_method === 'margin_then_timing' && (
                        <> <strong>Regular season margin</strong> (higher is better), with <strong>prediction submission time</strong> (earlier is better) used if margins were equal.</>
                      )}
                      {playoff.tiebreaker_method === 'regular_season_margin' && (
                        <> <strong>Regular season margin</strong> (higher is better).</>
                      )}
                      {playoff.tiebreaker_method === 'prediction_timing' && (
                        <> <strong>Prediction submission time</strong> (earlier is better).</>
                      )}
                    </p>
                  </div>
                )}
                <div className="results-list">
                  {sortedPredictions.map(pred => {
                    // Check if this user was part of a tie (has same score_difference as others)
                    const scoreDiff = pred.score_difference != null ? parseFloat(pred.score_difference) : null;
                    const key = scoreDiff != null && !isNaN(scoreDiff) ? scoreDiff.toFixed(1) : null;
                    const tiedUsers = key != null ? (scoreGroups[key] || []) : [];
                    const wasTied = tiedUsers.length > 1;
                    
                    return (
                      <div key={pred.id} className="result-item">
                        <span className="rank">#{pred.final_rank}</span>
                        <span className="username">{pred.username}</span>
                        <span className="score-diff">Diff: {pred.score_difference?.toFixed(1)}</span>
                        <span className="margin" style={{ marginLeft: '10px', fontSize: '14px', color: '#aaa' }}>
                          Margin: {(pred.accumulated_margin || 0).toFixed(2)}
                        </span>
                        {wasTied && playoff.tiebreaker_method && (
                          <span className="tiebreaker-note" style={{ 
                            fontSize: '11px', 
                            color: '#ffd700', 
                            marginLeft: '10px',
                            fontStyle: 'italic',
                            opacity: 0.9
                          }}>
                            (Tied - Tie-Breaker Applied)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default PlayoffStatusPage;

